import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UsersIcon, CreditCardIcon, BarChart3Icon, SparklesIcon, ChevronRightIcon, SettingsIcon, ShieldCheckIcon, FileTextIcon, CloudIcon, LightbulbIcon, CoinsIcon, ClockIcon, TrendingUpIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
type Bilingual = {
  en: string;
  fr: string;
};
const MILESTONES: {
  icon: typeof UsersIcon;
  title: Bilingual;
  body: Bilingual;
}[] = [{
  icon: UsersIcon,
  title: {
    en: 'More adoption',
    fr: 'Plus d’adoption'
  },
  body: {
    en: 'Engaged people and teams',
    fr: 'Citoyens et équipes engagés'
  }
}, {
  icon: CreditCardIcon,
  title: {
    en: 'More interactions and transactions',
    fr: 'Plus d’interactions et de transactions'
  },
  body: {
    en: 'Services people actually use',
    fr: 'Des services réellement utilisés'
  }
}, {
  icon: BarChart3Icon,
  title: {
    en: 'Greater operational efficiency',
    fr: 'Plus d’efficacité opérationnelle'
  },
  body: {
    en: 'Teams able to accomplish more',
    fr: 'Des équipes qui en font plus'
  }
}];
const PHASES: {
  number: string;
  title: Bilingual;
  body: Bilingual;
  atnaTitle: Bilingual;
  atnaBody: Bilingual;
}[] = [{
  number: '01',
  title: {
    en: 'Operational foundation',
    fr: 'Fondation opérationnelle'
  },
  body: {
    en: 'Start with the priorities that create immediate value.',
    fr: 'Commencez par les priorités qui créent immédiatement de la valeur.'
  },
  atnaTitle: {
    en: 'ATNA guides users',
    fr: 'ATNA accompagne les usagers'
  },
  atnaBody: {
    en: 'Answers, guides and simplifies access to services.',
    fr: 'Répond, oriente et simplifie l’accès aux services.'
  }
}, {
  number: '02',
  title: {
    en: 'Services & transactions',
    fr: 'Services et transactions'
  },
  body: {
    en: 'Add new capabilities as your needs evolve.',
    fr: 'Ajoutez de nouvelles capacités lorsque vos besoins évoluent.'
  },
  atnaTitle: {
    en: 'ATNA copilots teams',
    fr: 'ATNA copilote les équipes'
  },
  atnaBody: {
    en: 'Suggests, verifies and accelerates processing.',
    fr: 'Suggère, vérifie et accélère le traitement.'
  }
}, {
  number: '03',
  title: {
    en: 'Extended ecosystem',
    fr: 'Écosystème étendu'
  },
  body: {
    en: 'Connect more services, systems and partners.',
    fr: 'Connectez davantage de services, de systèmes et de partenaires.'
  },
  atnaTitle: {
    en: 'ATNA orchestrates the ecosystem',
    fr: 'ATNA orchestre l’écosystème'
  },
  atnaBody: {
    en: 'Connects, optimizes and evolves workflows.',
    fr: 'Met en relation, optimise et fait évoluer les processus.'
  }
}];
const BAND_ITEMS: {
  icon: typeof ShieldCheckIcon;
  line1: Bilingual;
  line2: Bilingual;
}[] = [{
  icon: ShieldCheckIcon,
  line1: {
    en: 'Security',
    fr: 'Sécurité'
  },
  line2: {
    en: '& resilience',
    fr: 'et résilience'
  }
}, {
  icon: FileTextIcon,
  line1: {
    en: 'Privacy (Law 25)',
    fr: 'Loi 25'
  },
  line2: {
    en: '& governance',
    fr: 'et gouvernance'
  }
}, {
  icon: CloudIcon,
  line1: {
    en: 'Regular',
    fr: 'Mises à jour'
  },
  line2: {
    en: 'updates',
    fr: 'régulières'
  }
}, {
  icon: BarChart3Icon,
  line1: {
    en: 'New capabilities',
    fr: 'Nouvelles capacités'
  },
  line2: {
    en: 'delivered continuously',
    fr: 'déployées en continu'
  }
}, {
  icon: LightbulbIcon,
  line1: {
    en: 'Informed decisions',
    fr: 'Décisions éclairées'
  },
  line2: {
    en: 'Data · analytics · recommendations',
    fr: 'Données · analyses · recommandations'
  }
}];
const BENEFITS: {
  icon: typeof CoinsIcon;
  title: Bilingual;
  body: Bilingual;
}[] = [{
  icon: CoinsIcon,
  title: {
    en: 'Costs that can decrease',
    fr: 'Des coûts qui peuvent diminuer'
  },
  body: {
    en: 'Fewer redundant tools, duplicate data entry and manual tasks.',
    fr: 'Moins d’outils redondants, de ressaisies et de tâches manuelles.'
  }
}, {
  icon: ClockIcon,
  title: {
    en: 'Faster to deploy',
    fr: 'Plus rapide à déployer'
  },
  body: {
    en: 'Every new capability builds on what is already in place.',
    fr: 'Chaque nouvelle capacité réutilise ce qui est déjà en place.'
  }
}, {
  icon: UsersIcon,
  title: {
    en: 'A unified experience',
    fr: 'Une expérience unifiée'
  },
  body: {
    en: 'One identity across all services.',
    fr: 'Une même identité pour tous les services.'
  }
}, {
  icon: TrendingUpIcon,
  title: {
    en: 'More value over time',
    fr: 'Plus de valeur au fil du temps'
  },
  body: {
    en: 'The platform evolves with your needs.',
    fr: 'La plateforme évolue avec vos besoins.'
  }
}];
export function CompoundingFoundation() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  const railLabel = 'font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#121212]/45 leading-[1.7]';
  return <div className="max-w-[1320px] mx-auto" data-id="element-3841" data-mp="3brac3zz6pu2b">
      {/* Header */}
      <motion.div {...reveal} className="max-w-3xl" data-id="element-3842" data-mp="2o0505vwd7lxa">
        <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#121212] uppercase" data-id="element-3843" data-mp="2jy1igpj3rl3k">
          {isFR ? 'Une fondation qui évolue avec vous' : 'A foundation that evolves with you'}
        </p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#121212] leading-[1.12] tracking-tight mt-5" data-id="element-3844" data-mp="2sxethmmvkhqe">
          <span className="block" data-id="element-3845" data-mp="d60qtqzxvij3">
            {isFR ? 'Activez ce dont vous avez besoin.' : 'Activate what you need.'}
          </span>
          <span className="block" data-id="element-3846" data-mp="g96t8qocpfia">
            {isFR ? 'La plateforme continue d’avancer.' : 'The platform keeps moving forward.'}
          </span>
        </h2>
        <p className="font-sans text-base lg:text-lg text-[#5B6478] leading-relaxed mt-5" data-id="element-3847" data-mp="p9lgvwosf6oy">
          {isFR ? 'La même identité, les mêmes profils, relations, consentements, droits et connexions.' : 'The same identity, profiles, relationships, consents, rights and connections.'}
          <br className="hidden sm:block" data-id="element-3848" data-mp="1jr7xq7la29z" />{' '}
          {isFR ? 'Chaque nouvelle capacité s’appuie sur ce qui est déjà en place.' : 'Every new capability builds on what is already in place.'}
        </p>
      </motion.div>

      {/* ---------- Desktop / tablet composition ---------- */}
      <div className="hidden md:block" data-id="element-3849" data-mp="3a0c6ed2lhcyu">
        {/* Growth curve */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.06,
        ease: EASE
      }} className="grid grid-cols-[9rem_minmax(0,1fr)] gap-6 mt-10" data-id="element-3850" data-mp="39xzqs6kb7eoy">
          <div className="flex items-end pb-2" data-id="element-3851" data-mp="37dbieeyvq3pe">
            <p className={railLabel} data-id="element-3852" data-mp="32g1keaxfcjpy">
              {isFR ? <>
                  Une valeur
                  <br data-id="element-3853" data-mp="2xfx4am0fhr09" />
                  qui augmente
                  <br data-id="element-3854" data-mp="2sidrzrnajoay" />
                  avec l’usage
                </> : <>
                  Value grows
                  <br data-id="element-3855" data-mp="2ohbq131zoy5a" />
                  with usage
                </>}
            </p>
          </div>

          <div className="relative h-[13rem]" data-id="element-3856" data-mp="2joinmltzu1wd">
            {/* curve */}
            <svg viewBox="0 0 1000 208" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true" data-id="element-3857" data-mp="2ers3nnf2im5o">
              <path d="M0 186 C 240 176, 430 148, 620 100 S 900 34, 1000 8" fill="none" stroke="#4F46E5" strokeOpacity="0.55" strokeWidth="1.25" vectorEffect="non-scaling-stroke" data-id="element-3858" data-mp="24r9eqe8ncfgw" />
              <path d="M978 2 L1000 8 L980 20" fill="none" stroke="#4F46E5" strokeOpacity="0.55" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" data-id="element-3859" data-mp="3tlp480usbw8b" />
            </svg>

            {/* milestones */}
            {MILESTONES.map((milestone, index) => {
            const Icon = milestone.icon;
            const left = ['18%', '43%', '71%'][index];
            const top = ['84%', '66%', '36%'][index];
            return <div key={milestone.title.en} className="absolute" style={{
              left,
              top
            }} data-id="element-3860" data-mp="30yku5hgu4rpo" data-mpi={index}>
                  <span className="absolute -left-[5px] -top-[5px] w-2.5 h-2.5 rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-3861" data-mp="2r0ett4aoftmx" />
                  <span className="absolute left-0 top-0 w-px bg-[#121212]/10" style={{
                height: `calc(13rem - ${top})`
              }} aria-hidden="true" data-id="element-3862" data-mp="23crpwj22r6xf" />
                  <div className="absolute left-4 top-1 flex items-start gap-2.5 w-[17rem]" data-id="element-3863" data-mp="2ccwxat79q5bl">
                    <Icon className="w-[18px] h-[18px] text-[#4F46E5] shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" data-id="element-3864" data-mp="2r9xiseyv941m" />
                    <span data-id="element-3865" data-mp="301pl22dql6v4">
                      <span className="block font-sans text-sm font-medium text-[#121212] leading-snug" data-id="element-3866" data-mp="301zczvicdo3g">
                        {pick(milestone.title)}
                      </span>
                      <span className="block font-sans text-[13px] text-[#5B6478] leading-snug mt-0.5" data-id="element-3867" data-mp="3a1qbe8a0wn8t">
                        {pick(milestone.body)}
                      </span>
                    </span>
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Phase cards */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: EASE
      }} className="grid grid-cols-[9rem_minmax(0,1fr)] gap-6 mt-2" data-id="element-3868" data-mp="3sn27fhf6mscn">
          <div className="flex items-center" data-id="element-3869" data-mp="3jm40aq9g64pu">
            <p className={railLabel} data-id="element-3870" data-mp="k9d307swlm6y">
              {isFR ? <>
                  Des capacités
                  <br data-id="element-3871" data-mp="g8t5h34f4gka" />
                  activées
                  <br data-id="element-3872" data-mp="b8et58vsakgg" />
                  selon vos
                  <br data-id="element-3873" data-mp="b73utol3y4wq" />
                  priorités
                </> : <>
                  Capabilities
                  <br data-id="element-3874" data-mp="2vymdl951hwtq" />
                  activated around
                  <br data-id="element-3875" data-mp="2w078izv9nhs4" />
                  your priorities
                </>}
            </p>
          </div>

          <div className="flex items-stretch gap-2" data-id="element-3876" data-mp="2r1m932nu924q">
            {PHASES.map((phase, index) => <React.Fragment key={phase.number} data-id="element-3877" data-mp="2gthzpa4wyym7" data-mpi={index}>
                {index > 0 && <div className="flex items-center shrink-0" data-id="element-3878" data-mp="2luyxuxw0k6z3">
                    <ChevronRightIcon className="w-5 h-5 text-[#121212]/25" strokeWidth={1.75} aria-hidden="true" data-id="element-3879" data-mp="2qtiimuyvfcpv" />
                  </div>}
                <article className="flex-1 rounded-2xl border border-[#E4E6EC] bg-white px-6 py-6 flex flex-col" data-id="element-3880" data-mp="2zlu6qa6iio2o">
                  <div className="flex items-start gap-4" data-id="element-3881" data-mp="34n06grylbblp">
                    <span className="w-10 h-10 shrink-0 rounded-full bg-[#4F46E5] text-white font-sans text-sm font-semibold flex items-center justify-center" data-id="element-3882" data-mp="39ll54my71n81">
                      {phase.number}
                    </span>
                    <div data-id="element-3883" data-mp="1f8bswne9jnm3">
                      <h3 className="font-serif text-xl text-[#121212] leading-snug" data-id="element-3884" data-mp="1k5bvgobwm88g">
                        {pick(phase.title)}
                      </h3>
                      <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-1.5" data-id="element-3885" data-mp="1c55wq5gg2mvc">
                        {pick(phase.body)}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-[#E4E6EC] mt-auto pt-5 flex items-start gap-4" data-id="element-3886" data-mp="1m0qutsie72zt">
                    <span className="w-10 shrink-0 flex justify-center" data-id="element-3887" data-mp="1h5toitchz7qm">
                      <SparklesIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-3888" data-mp="1v2758097j541" />
                    </span>
                    <div data-id="element-3889" data-mp="24ve5x7t05wxs">
                      <p className="font-sans text-[15px] font-medium text-[#121212] leading-snug" data-id="element-3890" data-mp="1zui738nid0z8">
                        {pick(phase.atnaTitle)}
                      </p>
                      <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-1" data-id="element-3891" data-mp="2drn82u8d79nm">
                        {pick(phase.atnaBody)}
                      </p>
                    </div>
                  </div>
                </article>
              </React.Fragment>)}
          </div>
        </motion.div>
      </div>

      {/* ---------- Mobile composition ---------- */}
      <div className="md:hidden mt-10" data-id="element-3892" data-mp="2jfqs59sfynxg">
        <p className={railLabel} data-id="element-3893" data-mp="2oev05x3mqfm7">
          {isFR ? 'Une valeur qui augmente avec l’usage' : 'Value grows with usage'}
        </p>
        <ol className="relative mt-5 pl-6 space-y-6" data-id="element-3894" data-mp="29p5srk4y1fdh">
          <span className="absolute left-[3px] top-2 bottom-2 w-px bg-[#4F46E5]/35" aria-hidden="true" data-id="element-3895" data-mp="2egxd3cxiaov6" />
          {MILESTONES.map(milestone => {
          const Icon = milestone.icon;
          return <li key={milestone.title.en} className="relative" data-id="element-3896" data-mp="24rxazknpjdcr">
                <span className="absolute -left-6 top-1.5 w-[7px] h-[7px] rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-3897" data-mp="1qqt8p3chul34" />
                <div className="flex items-start gap-2.5" data-id="element-3898" data-mp="1vsa78s7i9vgj">
                  <Icon className="w-[18px] h-[18px] text-[#4F46E5] shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" data-id="element-3899" data-mp="2y2kbe5uykjwi" />
                  <div data-id="element-3900" data-mp="2p9z4po4lq3o1">
                    <p className="font-sans text-sm font-medium text-[#121212] leading-snug" data-id="element-3901" data-mp="2p9fku1ve557d">
                      {pick(milestone.title)}
                    </p>
                    <p className="font-sans text-[13px] text-[#5B6478] leading-snug mt-0.5" data-id="element-3902" data-mp="2fe5jlgcth1un">
                      {pick(milestone.body)}
                    </p>
                  </div>
                </div>
              </li>;
        })}
        </ol>

        <p className={`${railLabel} mt-10`} data-id="element-3903" data-mp="21duq05k4h95s">
          {isFR ? 'Des capacités activées selon vos priorités' : 'Capabilities activated around your priorities'}
        </p>
        <div className="mt-5 space-y-4" data-id="element-3904" data-mp="3vrm71q4s6vb9">
          {PHASES.map(phase => <article key={phase.number} className="rounded-2xl border border-[#E4E6EC] bg-white px-5 py-5" data-id="element-3905" data-mp="3vpjd4v0ici19">
              <div className="flex items-start gap-4" data-id="element-3906" data-mp="c3sfw1bk7bdf">
                <span className="w-10 h-10 shrink-0 rounded-full bg-[#4F46E5] text-white font-sans text-sm font-semibold flex items-center justify-center" data-id="element-3907" data-mp="c2hdo5xribof">
                  {phase.number}
                </span>
                <div data-id="element-3908" data-mp="3kciwr3or21qh">
                  <h3 className="font-serif text-xl text-[#121212] leading-snug" data-id="element-3909" data-mp="3kd2hes5sb487">
                    {pick(phase.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-1.5" data-id="element-3910" data-mp="3pdgddbu42npv">
                    {pick(phase.body)}
                  </p>
                </div>
              </div>
              <div className="border-t border-[#E4E6EC] mt-4 pt-4 flex items-start gap-4" data-id="element-3911" data-mp="27z62pp85vzc">
                <span className="w-10 shrink-0 flex justify-center" data-id="element-3912" data-mp="19jof968l2aah">
                  <SparklesIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-3913" data-mp="1dl1mnkrbqlji" />
                </span>
                <div data-id="element-3914" data-mp="35mb3146pba2k">
                  <p className="font-sans text-[15px] font-medium text-[#121212] leading-snug" data-id="element-3915" data-mp="35munosnqkcka">
                    {pick(phase.atnaTitle)}
                  </p>
                  <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-1" data-id="element-3916" data-mp="3ensqj7mfue1a">
                    {pick(phase.atnaBody)}
                  </p>
                </div>
              </div>
            </article>)}
        </div>
      </div>

      {/* Continuous platform band — runs past the right edge */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.12,
      ease: EASE
    }} className="relative mt-4 md:mt-3" data-id="element-3917" data-mp="30pb3zd89r7ky">
        <div className="rounded-3xl md:rounded-[2rem] bg-[#4F46E5]/[0.06] px-6 md:px-8 py-6 md:mr-[-3rem] md:pr-16" data-id="element-3918" data-mp="287yuukwvg6m2">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6" data-id="element-3919" data-mp="239f66cqw8gq0">
            <div className="flex items-start gap-4 lg:w-[22rem] shrink-0" data-id="element-3920" data-mp="iqux0wkcsjim">
              <SettingsIcon className="w-7 h-7 text-[#121212] shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-3921" data-mp="ipju0yyqffsk" />
              <div data-id="element-3922" data-mp="9rg7bzc0gcz6">
                <p className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212] leading-[1.6]" data-id="element-3923" data-mp="9rzr7ll81bfu">
                  {isFR ? <>
                      Plateforme
                      <br data-id="element-3924" data-mp="12cptcvje799i" />
                      en évolution continue
                    </> : <>
                      A continuously
                      <br data-id="element-3925" data-mp="sg4ny93gnw55" />
                      evolving platform
                    </>}
                </p>
                <p className="font-sans text-sm text-[#5B6478] mt-1.5" data-id="element-3926" data-mp="xhkcib4ukksa">
                  {isFR ? 'Toujours à jour. Toujours prête pour la suite.' : 'Always current. Always ready for what’s next.'}
                </p>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-0 lg:divide-x lg:divide-[#121212]/10" data-id="element-3927" data-mp="dlsorlmc7bz4">
              {BAND_ITEMS.map(item => {
              const Icon = item.icon;
              return <div key={item.line1.en} className="flex items-center gap-2.5 lg:px-4 first:lg:pl-0" data-id="element-3928" data-mp="3ux299ab50izh">
                    <Icon className="w-[18px] h-[18px] text-[#121212] shrink-0" strokeWidth={1.6} aria-hidden="true" data-id="element-3929" data-mp="rfk08zjmp5xh" />
                    <span className="font-sans text-[13px] leading-snug" data-id="element-3930" data-mp="hpqo9xfkqosp">
                      <span className="block text-[#121212]" data-id="element-3931" data-mp="hno25ohjlft9">
                        {pick(item.line1)}
                      </span>
                      <span className="block text-[#5B6478]" data-id="element-3932" data-mp="1aeo3tssei7hr">
                        {pick(item.line2)}
                      </span>
                    </span>
                  </div>;
            })}
            </div>

            <ArrowRightIcon className="hidden lg:block w-6 h-6 text-[#4F46E5] shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-3933" data-mp="1mpf4tjh1iuf4" />
          </div>
        </div>
      </motion.div>

      {/* Benefits */}
      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.14,
      ease: EASE
    }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x lg:divide-[#E4E6EC] mt-12 pt-10 border-t border-[#E4E6EC]" data-id="element-3934" data-mp="21lwem69l00zk">
        {BENEFITS.map(benefit => {
        const Icon = benefit.icon;
        return <div key={benefit.title.en} className="flex items-start gap-3.5 lg:px-7 first:lg:pl-0" data-id="element-3935" data-mp="2fct8e0f8uo87">
              <Icon className="w-6 h-6 text-[#121212] shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-3936" data-mp="2pbig50dfesaq" />
              <div data-id="element-3937" data-mp="3nvxtd9bydmqq">
                <h3 className="font-serif text-lg text-[#121212] leading-snug" data-id="element-3938" data-mp="3nwhd8vl5yl7e">
                  {pick(benefit.title)}
                </h3>
                <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-1.5" data-id="element-3939" data-mp="1mn29z6wy8vs">
                  {pick(benefit.body)}
                </p>
              </div>
            </div>;
      })}
      </motion.div>

      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.16,
      ease: EASE
    }} className="flex justify-start lg:justify-end mt-8" data-id="element-3940" data-mp="an1s9butoc5m">
        <Link to="/platform" className="inline-flex items-center gap-2 font-sans text-[15px] font-medium text-[#4F46E5] underline underline-offset-4 decoration-[#4F46E5]/40 hover:decoration-[#4F46E5] transition-colors duration-200" data-id="element-3941" data-mp="kd4i536nd9zq">
          {isFR ? 'Voir la plateforme complète' : 'Explore the full platform'}
          <ArrowRightIcon className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3942" data-mp="ydf9067yiokw" />
        </Link>
      </motion.div>
    </div>;
}